import {
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandDialog,
  CommandShortcut,
} from "@/components/shadcn/custom-command";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useRouter } from "next/navigation";
import Fuse from "fuse.js";
import {
  BookOpen,
  Bot,
  BotMessageSquare,
  History,
  LayoutGrid,
  Inbox,
  Settings,
  Table2,
  Workflow,
} from "lucide-react";
import { isCaseManagementEnabled } from "@/utils/modules";
import {
  ISectionGate,
  useIsGateAllowed,
} from "@/components/Layouts/pageSections";
import {
  APP_LIBRARY_PERMS,
  CASES_PERMS,
  MANAGE_WORKSPACE_PERMS,
  P,
  PAST_USAGE_PERMS,
  RESOURCE_VIEW_PERMS,
} from "@/constants/permissions";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { NoxusIcon } from "../UI/NoxusIcon";
import { WorkshopRecent } from "@/generated";
import { keepPreviousData } from "@tanstack/react-query";
import { Spinner } from "../UI/Spinner";
import { useInboxes } from "@/components/Spotflow/Inbox/useInboxes";
import { getModifierKey } from "@/utils/generic";
import {
  CreateNewToolId,
  useCreateNewTool,
} from "@/components/Dialogs/useCreateNewTool";

const USED_KEYS = ["F", "O", "B", "H", "S", "U", "W", "T", "D", "L"];

const SHIFT_SHORTCUTS: Record<string, string> = {
  F: "/?tab=flows",
  O: "/?tab=agents",
  K: "/?tab=knowledge-bases",
};

interface IAmazingDialogOption {
  text: string;
  toolId?: string;
  href?: string;
  actionParam?: string;
  icon: React.ReactNode;
  category: "create" | "navigate";
  commandKey?: string;
  keywords?: string[];
  // Who may see the command, the same gate as the page it opens.
  gate?: ISectionGate;
}

const CREATE_GATE: ISectionGate = { requiredPermissions: [P.RESOURCE_EDIT] };
const WORKSHOP_GATE: ISectionGate = {
  requiredPermissions: RESOURCE_VIEW_PERMS,
};
const CASES_GATE: ISectionGate = { requiredPermissions: CASES_PERMS };
// What the recent-items endpoint accepts; without it there are no suggestions.
const RECENT_GATE: ISectionGate = {
  requiredPermissions: [P.RESOURCE_READ, P.RESOURCE_RUN, P.RESOURCE_EDIT],
};

const createOptions = (): IAmazingDialogOption[] => {
  const modifierKey = getModifierKey();

  return [
    {
      text: "Create Flow",
      toolId: "agentic-workflow",
      icon: <Workflow />,
      category: "create",
      commandKey: "L",
      keywords: ["workflow", "automation", "agentic"],
      gate: CREATE_GATE,
    },
    {
      text: "Create Agent",
      toolId: "co-worker",
      icon: <BotMessageSquare />,
      category: "create",
      commandKey: "O",
      keywords: ["assistant", "bot", "ai", "agent", "coworker", "co-worker"],
      gate: CREATE_GATE,
    },
    {
      text: "Create Knowledge Base",
      toolId: "knowledge-base",
      icon: <BookOpen />,
      category: "create",
      commandKey: "B",
      keywords: ["kb", "data", "documents", "files", "knowledge", "kbs"],
      gate: CREATE_GATE,
    },
    {
      text: "Create Table",
      toolId: "table-tool",
      icon: <Table2 />,
      category: "create",
      keywords: ["table", "tables", "data", "database", "rows", "spreadsheet"],
      gate: CREATE_GATE,
    },
    {
      text: "Flows",
      actionParam: "/?tab=flows",
      icon: <Workflow />,
      category: "navigate",
      // commandKey: "⇧+F",
      keywords: ["workflows", "automations"],
      gate: WORKSHOP_GATE,
    },
    {
      text: "Agents",
      actionParam: "/?tab=agents",
      icon: <BotMessageSquare />,
      category: "navigate",
      // commandKey: "⇧+O",
      keywords: [
        "co-workers",
        "coworkers",
        "co-worker",
        "coworker",
        "assistants",
        "bots",
        "agents",
      ],
      gate: WORKSHOP_GATE,
    },
    {
      text: "Knowledge Bases",
      actionParam: "/?tab=knowledge-bases",
      icon: <BookOpen />,
      category: "navigate",
      // commandKey: "⇧+K",
      keywords: ["kb", "data", "documents", "files", "knowledge", "kbs"],
      gate: WORKSHOP_GATE,
    },
    {
      text: "Tables",
      actionParam: "/?tab=tables",
      icon: <Table2 />,
      category: "navigate",
      keywords: ["table", "tables", "data", "database", "rows", "spreadsheet"],
      gate: WORKSHOP_GATE,
    },
    {
      text: "App library",
      actionParam: "/apps",
      icon: <LayoutGrid />,
      category: "navigate",
      keywords: ["apps", "run", "deployments"],
      gate: { requiredPermissions: APP_LIBRARY_PERMS },
    },
    {
      text: "Past usage",
      actionParam: "/past-usage",
      icon: <History />,
      category: "navigate",
      commandKey: "U",
      keywords: ["history", "usage", "billing", "analytics"],
      gate: { requiredPermissions: PAST_USAGE_PERMS },
    },
    {
      text: "Manage workspace",
      actionParam: "/manage-workspace",
      icon: <Settings />,
      category: "navigate",
      commandKey: "S",
      keywords: [
        "settings",
        "preferences",
        "config",
        "configuration",
        "control",
      ],
      gate: { requiredPermissions: MANAGE_WORKSPACE_PERMS },
    },
    {
      text: "Documentation",
      href: "https://docs.noxus.ai/core/overview",
      icon: <BookOpen />,
      category: "navigate",
      commandKey: "D",
      keywords: ["docs", "help", "guide", "manual", "tutorial"],
    },
  ];
};

const AmazingDialog = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const router = useRouter();

  const [inputValue, setInputValue] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [pendingToolId, setPendingToolId] = useState<CreateNewToolId | null>(
    null
  );
  const { spotGroup } = useSpotUser();
  const caseManagementEnabled = isCaseManagementEnabled(spotGroup);
  const isGateAllowed = useIsGateAllowed();

  const { createNewTool, isLoading: isCreatingTool } = useCreateNewTool({
    onCreated: () => {
      setPendingToolId(null);
      onOpenChange(false);
    },
    onError: () => {
      setPendingToolId(null);
      onOpenChange(false);
    },
  });

  const debouncedSetSearchValue = useMemo(
    () => debounce(setValue, 200),
    [setValue]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      debouncedSetSearchValue(value);
    },
    [debouncedSetSearchValue]
  );

  const {
    data: recentData,
    isLoading,
    isFetching,
  } = useRemoteData(
    DATA_KEYS.WORKSHOP_RECENT,
    {
      path: { group_id: spotGroup?.id },
      query: {
        query: value,
      },
    },
    {
      placeholderData: keepPreviousData,
      enabled: isOpen && isGateAllowed(RECENT_GATE),
    }
  );

  const canSeeCases = caseManagementEnabled && isGateAllowed(CASES_GATE);
  const { inboxes } = useInboxes(canSeeCases);
  const options = useMemo(
    () => [
      ...createOptions(),
      ...inboxes.map(
        (inbox): IAmazingDialogOption => ({
          text: inbox.name,
          actionParam: `/inbox/${inbox.id}`,
          icon: <Inbox />,
          category: "navigate",
          keywords: ["inbox", "inboxes", "cases"],
          gate: CASES_GATE,
        })
      ),
    ],
    [inboxes]
  );
  const modifierKey = useMemo(() => getModifierKey(), []);

  const fuseOptions = useMemo(
    () => ({
      keys: [
        { name: "text", weight: 0.7 },
        { name: "keywords", weight: 0.3 },
      ],
      threshold: 0.4,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 1,
    }),
    []
  );

  const allowedOptions = useMemo(() => {
    return options.filter(
      (option) => !option.gate || isGateAllowed(option.gate)
    );
  }, [options, isGateAllowed]);

  const createCommands = allowedOptions
    .filter((option) => option.category === "create")
    .slice(0, 4);

  const fuseIndex = useMemo(() => {
    return Fuse.createIndex(fuseOptions.keys, allowedOptions);
  }, [allowedOptions, fuseOptions.keys]);

  const fuse = useMemo(() => {
    return new Fuse(allowedOptions, fuseOptions, fuseIndex);
  }, [allowedOptions, fuseOptions, fuseIndex]);

  const filteredOptions: IAmazingDialogOption[] = useMemo(() => {
    if (inputValue === "") {
      return allowedOptions;
    }

    const results = fuse.search(inputValue);
    return results.map((result: any) => result.item);
  }, [inputValue, fuse, allowedOptions]);

  const handleCreateNewTool = useCallback(
    (toolId: CreateNewToolId) => {
      setPendingToolId(toolId);
      createNewTool(toolId);
    },
    [createNewTool]
  );

  const isLoadingCreation = useMemo(
    () => ({
      "agentic-workflow":
        isCreatingTool && pendingToolId === "agentic-workflow",
      "co-worker": isCreatingTool && pendingToolId === "co-worker",
      "knowledge-base": isCreatingTool && pendingToolId === "knowledge-base",
    }),
    [isCreatingTool, pendingToolId]
  );

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only process if Command/Meta key (Mac) or Control key (Windows) is pressed
      if (
        !(event.metaKey || event.ctrlKey) ||
        !USED_KEYS.includes(event.key.toUpperCase())
      ) {
        return;
      }

      // Handle number keys 1-9 for recent items
      if (event.key >= "1" && event.key <= "9") {
        const index = parseInt(event.key) - 1;

        // Handle recent items shortcuts (⌘1, ⌘2, etc.)
        if (recentData && index < recentData.length) {
          event.preventDefault();
          const value = recentData[index];
          let path = "";

          if (value.type === "flow") {
            path = `/flows/${value.id}`;
          } else if (value.type === "assistant") {
            path = `/assistant/${value.id}`;
          } else if (value.type === "knowledge_base") {
            path = `/data-hub/${value.id}`;
          } else if (value.type === "table") {
            path = `/tables/${value.id}`;
          } else if (value.type === "inbox") {
            path = `/inbox/${value.id}`;
          }

          router.push(path);
          onOpenChange(false);
        }
        return;
      }

      // The shortcut of a command the user cannot see does nothing.
      const key = event.key.toUpperCase();
      const shiftTarget = SHIFT_SHORTCUTS[key];
      if (event.shiftKey && !shiftTarget) return;
      const option = event.shiftKey
        ? allowedOptions.find(
            (candidate) => candidate.actionParam === shiftTarget
          )
        : allowedOptions.find((candidate) => candidate.commandKey === key);
      if (!option) return;
      event.preventDefault();
      if (option.toolId) {
        handleCreateNewTool(option.toolId as CreateNewToolId);
        return;
      }
      if (option.href) {
        window.open(option.href, "_blank", "noopener noreferrer");
      } else if (option.actionParam) {
        router.push(option.actionParam);
      }
      onOpenChange(false);
    },
    [recentData, router, onOpenChange, handleCreateNewTool, allowedOptions]
  );

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue("");
      setValue("");
    }
  }, [isOpen]);

  const hasResults =
    (filteredOptions?.length ?? 0) > 0 || (recentData?.length ?? 0) > 0;
  // Recent tools, plus the matches while typing; no heading over nothing.
  const showSuggested =
    (!isLoading && (recentData?.length ?? 0) > 0) ||
    (inputValue !== "" && (filteredOptions?.length ?? 0) > 0);

  // Always highlight the first item when results change
  const [selectedValue, setSelectedValue] = useState("");

  useEffect(() => {
    setSelectedValue("");
  }, [inputValue, recentData]);

  return (
    // Not fixed here: the dialog pins itself to the window, or to the page
    // beside Genie while Genie is open, and centres in either. Offsets and
    // widths are % so they measure against whichever it is.
    <CommandDialog
      title="Quick Search"
      description="Type a command or search..."
      showCloseButton={false}
      shouldFilter={false}
      open={isOpen}
      onOpenChange={onOpenChange}
      commandValue={selectedValue}
      onCommandValueChange={setSelectedValue}
      commandClass="h-full border-none shadow-none rounded-none"
      contentClassName="top-[14%] left-[50%] translate-x-[-50%] translate-y-0 max-w-[min(750px,calc(100%-2rem))] sm:max-w-[min(750px,calc(100%-2rem))] w-full border shadow-[0_7px_24px_-7px_rgba(0,0,0,0.13),0_17px_52px_-13px_rgba(0,0,0,0.18)] bg-popover rounded-xl overflow-hidden p-0 data-[state=open]:duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-2 data-[state=closed]:duration-150 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-top-2"
    >
      {/* Search */}
      <CommandInput
        placeholder={"Type a command or search..."}
        onValueChange={handleInputChange}
        wrapperClassName="h-auto px-5 py-5 gap-3 [&_svg]:!size-[18px]"
        className="bg-transparent border-none focus:ring-0 h-auto text-[13px] leading-normal"
        autoFocus={true}
        loading={isFetching || inputValue !== value}
      />
      <div className="h-px bg-border shrink-0" />
      <div className="overflow-hidden">
        <CommandList className="h-[400px] overflow-y-auto scrollbar-on-hover">
          {!hasResults ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : (
            <Fragment>
              {showSuggested && (
                <>
                  <CommandGroup heading="Suggested">
                    {!isLoading &&
                      recentData?.slice(0, 2).map((value, index) => (
                        <CommandItem
                          key={`tools-${value.id}`}
                          value={`tools-${index}`}
                          disabled={isLoading}
                          onSelect={() => {
                            let path = "";
                            if (value.type === "flow") {
                              path = `/flows/${value.id}`;
                            } else if (value.type === "assistant") {
                              path = `/assistant/${value.id}`;
                            } else if (value.type === "knowledge_base") {
                              path = `/data-hub/${value.id}`;
                            } else if (value.type === "table") {
                              path = `/tables/${value.id}`;
                            } else if (value.type === "inbox") {
                              path = `/inbox/${value.id}`;
                            }

                            router.push(path);
                            onOpenChange(false);
                          }}
                          className="flex items-center gap-2 cursor-pointer py-2 px-3 text-[13px] [&_svg]:!size-4"
                        >
                          <ElementName element={value} />
                          <CommandShortcut>{`${modifierKey}${index + 1}`}</CommandShortcut>
                        </CommandItem>
                      ))}

                    {inputValue !== "" &&
                      filteredOptions.map((option, index) => (
                        <CommandItem
                          key={`search-${option.text}`}
                          value={`search-${index}`}
                          onSelect={() => {
                            if (option.toolId) {
                              handleCreateNewTool(
                                option.toolId as CreateNewToolId
                              );
                            }

                            if (option.href) {
                              window.open(
                                option.href,
                                "_blank",
                                "noopener noreferrer"
                              );
                              onOpenChange(false);
                            } else if (option.actionParam) {
                              router.push(option.actionParam);
                              onOpenChange(false);
                            }
                          }}
                          className="flex items-center gap-2 cursor-pointer py-2 px-3 text-[13px] [&_svg]:!size-4"
                        >
                          <IconWrapper>{option.icon}</IconWrapper>
                          <span>{option.text}</span>
                          {option.toolId &&
                            isLoadingCreation[
                              option.toolId as keyof typeof isLoadingCreation
                            ] && <Spinner size="sm" className="ml-auto" />}
                          {option?.commandKey && (
                            <CommandShortcut>{`${modifierKey}${option.commandKey}`}</CommandShortcut>
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              {/* Create Options */}
              {inputValue === "" && (
                <>
                  {createCommands.length > 0 && (
                    <>
                      <CommandGroup heading="Create new">
                        {createCommands.map((option, index) => (
                          <CommandItem
                            key={`create-${option.text}`}
                            value={`create-${index}`}
                            onSelect={() => {
                              if (option.toolId) {
                                handleCreateNewTool(
                                  option.toolId as CreateNewToolId
                                );
                              }
                            }}
                            className="flex items-center gap-2 cursor-pointer py-2 px-3 text-[13px] [&_svg]:!size-4"
                          >
                            <IconWrapper>{option.icon}</IconWrapper>
                            <span>{option.text}</span>
                            {option.toolId &&
                              isLoadingCreation[
                                option.toolId as keyof typeof isLoadingCreation
                              ] && <Spinner size="sm" className="ml-auto" />}
                            {option?.commandKey && (
                              <CommandShortcut>{`${modifierKey}${option.commandKey}`}</CommandShortcut>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandSeparator />
                    </>
                  )}
                  {/* Navigate Options */}
                  <CommandGroup heading="Navigate">
                    {allowedOptions
                      .filter((option) => option.category === "navigate")
                      .slice(0, 7)
                      .map((option, index) => (
                        <CommandItem
                          key={`navigate-${option.text}`}
                          value={`navigate-${index}`}
                          onSelect={() => {
                            if (option.href) {
                              window.open(
                                option.href,
                                "_blank",
                                "noopener noreferrer"
                              );
                            } else if (option.actionParam) {
                              router.push(option.actionParam);
                            }
                            onOpenChange(false);
                          }}
                          className="flex items-center gap-2 cursor-pointer py-2 px-3 text-[13px] [&_svg]:!size-4"
                        >
                          <IconWrapper>{option.icon}</IconWrapper>
                          <span>{option.text}</span>
                          {option?.commandKey && (
                            <CommandShortcut>{`${modifierKey}${option.commandKey}`}</CommandShortcut>
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </>
              )}
            </Fragment>
          )}
        </CommandList>
      </div>
    </CommandDialog>
  );
};

export default AmazingDialog;

const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-center w-6 h-6 shrink-0">
    {children}
  </div>
);

const ElementName = ({ element }: { element: WorkshopRecent }) => {
  const defaultImage =
    element.type === "assistant"
      ? "coworker"
      : element.type === "flow"
        ? "workflow"
        : element.type === "table"
          ? "table"
          : element.type === "inbox"
            ? "inbox"
            : "kb";

  const el_image = element.image?.replaceAll('"', "") || defaultImage;

  return (
    <div className="flex items-center gap-2">
      <div className="shrink-0">
        <NoxusIcon size="sm" icon={el_image} />
      </div>

      <p className="text-foreground">{element.name}</p>
    </div>
  );
};
